/**
 * 사업장·위치 메뉴의 규칙 — 이 서버의 화면을 **지역 › 사업장 › 위치** 세 층으로 정리한다(`screens/Locations.tsx`).
 * 층을 부르는 말은 고객사가 고른다(`siteLabels.ts` — 제조면 지역 › 공장 › 건물). 아래의 "공장" 은 둘째 층이다.
 *
 * - 위치는 화면마다 적은 문자열이다(화면 상세·화면 목록 일괄 변경·화면 추가). 그래서 "위치를 만든다" 는 일은 없고,
 *   있는 위치의 이름을 바꾸거나(같은 이름이면 합쳐진다) 지운다(그 화면들은 위치 미지정으로) — 서버가 그 이름의 화면
 *   전부(숨긴 것 포함)와 사업장 구성·모니터링 배치를 함께 옮긴다(`POST /displays/locations/rename`·`delete`).
 * - 지역·공장은 사업장 구성(`/api/v1/sites`)의 줄이다. 위치마다 정하고, 비워 둔 칸은 이 서버의 기본값(위치 없는
 *   줄)을, 그것도 없으면 "지역 미지정"·"본 서버" 를 따른다(`sites.ts` 의 `placeOf`). 모니터링의 사업장 현황이 같은
 *   줄로 그려진다.
 *
 * 2026-10-21 까지 이 일은 두 곳에 흩어져 있었다 — 지역·공장은 모니터링의 [사업장 구성] 창, 위치 이름 바꾸기·지우기는
 * 화면 목록 [⋯] 의 [위치 관리] 창. 어느 위치가 어느 공장에 있는지 한 표에서 보고 고칠 곳이 없었다.
 */
import type { DisplayLocation, FleetSummary, LocationChangeResult, SiteAddress, SiteEntry } from './api/client.js';
import { compareLocationNames } from './components/locationOptions.js';
import { DEFAULT_SITE_LABELS, unnamedRegion, withParticle, type SiteLabels } from './siteLabels.js';
import {
  addressLine,
  hasAddress,
  LOCAL_SITE_NAME,
  LOCAL_SOURCE,
  pointOf,
  type RegionNode,
  type SiteDraft,
  type SiteSource,
} from './sites.js';

/** 표 한 줄 — 위치(빈 문자열이면 위치를 적지 않은 화면들)와 그 화면 수. */
export interface LocationRow {
  name: string;
  /** 이 위치의 화면 수 — 숨긴 화면 포함(이름을 바꾸면 그 화면들도 함께 바뀐다). */
  displays: number;
  /** 그중 지금 켜진 화면 — 숨긴 화면은 세지 않는다(함대 요약의 위치별 집계). */
  online: number;
}

/**
 * 표의 줄 — 위치 목록(`/displays/locations`, 숨긴 화면 포함)에 위치별 집계(`/displays/summary?locations=1`)의 켜진 수를
 * 붙인다. 위치를 적지 않은 화면이 있으면 "위치 미지정" 줄(이름 빈 문자열)을 맨 뒤에 둔다 — 그 화면들의 지역·공장도
 * 여기서 정한다. 이름순.
 */
export function locationRows(
  listed: readonly DisplayLocation[],
  counted: NonNullable<FleetSummary['locations']>,
): LocationRow[] {
  const online = new Map(counted.map((entry) => [entry.name.trim(), entry.online]));
  const rows: LocationRow[] = listed
    .filter((item) => item.name.trim() !== '')
    .map((item) => ({ name: item.name, displays: item.display_count, online: online.get(item.name.trim()) ?? 0 }));
  const unplaced = counted.find((entry) => entry.name.trim() === '');
  if (unplaced && unplaced.total > 0) rows.push({ name: '', displays: unplaced.total, online: unplaced.online });
  return rows.sort((a, b) => (a.name === '' ? 1 : b.name === '' ? -1 : compareLocationNames(a.name, b.name)));
}

/** 이 서버를 사업장 나무의 출처 하나로 — 모니터링과 같은 나무(`buildSiteTree`)를 그리려고. */
export function localSiteSource(rows: readonly LocationRow[], asOf: string | null): SiteSource {
  return {
    id: LOCAL_SOURCE,
    name: LOCAL_SITE_NAME,
    kind: 'local',
    state: 'ok',
    stale: false,
    asOf,
    locations: rows.map((row) => ({
      name: row.name,
      total: row.displays,
      online: row.online,
      offline: Math.max(0, row.displays - row.online),
    })),
  };
}

/** 한 칸에서 고칠 것 — 지역·공장 글자, 주소(null 이면 지운다). */
export interface PlacePatch {
  region?: string;
  site?: string;
  address?: SiteAddress | null;
}

/** 고치는 중인 칸 — 기본값(`base`) 또는 위치 이름마다 지역·공장·주소. 저장된 값과 다른 것만 담는다. */
export interface PlaceEdits {
  base?: PlacePatch;
  locations: Record<string, PlacePatch>;
}

export const NO_EDITS: PlaceEdits = { locations: {} };

/** 저장된 표 입력(`draftFromEntries` 의 이 서버 몫)에 고치는 중인 칸을 얹는다. */
export function applyEdits(saved: SiteDraft, edits: PlaceEdits): SiteDraft {
  const buildings: SiteDraft['buildings'] = { ...saved.buildings };
  for (const [location, patch] of Object.entries(edits.locations)) {
    buildings[location] = { ...(buildings[location] ?? { region: '', site: '', address: null }), ...patch };
  }
  return { ...saved, ...edits.base, buildings };
}

/** 두 주소가 같은가 — 적지 않은 것끼리는 같다. 글자는 다듬어, 좌표는 값으로 견준다. */
export function sameAddress(a: SiteAddress | null | undefined, b: SiteAddress | null | undefined): boolean {
  if (!hasAddress(a) || !hasAddress(b)) return hasAddress(a) === hasAddress(b);
  const pa = pointOf(a);
  const pb = pointOf(b);
  return (
    a.postal_code.trim() === b.postal_code.trim() &&
    a.address.trim() === b.address.trim() &&
    a.address_detail.trim() === b.address_detail.trim() &&
    pa?.lat === pb?.lat &&
    pa?.lng === pb?.lng
  );
}

/** 저장할 것과 저장된 것이 다른 칸 수 — 0 이면 저장할 것이 없다. 앞뒤 공백은 다듬어 견준다(서버가 그렇게 둔다). */
export function changedPlaceCount(saved: SiteDraft, edited: SiteDraft): number {
  let count = 0;
  const differs = (a: string, b: string): boolean => a.trim() !== b.trim();
  if (differs(saved.region, edited.region)) count += 1;
  if (differs(saved.site, edited.site)) count += 1;
  if (!sameAddress(saved.address, edited.address)) count += 1;
  for (const [location, place] of Object.entries(edited.buildings)) {
    const before = saved.buildings[location] ?? { region: '', site: '', address: null };
    if (differs(before.region, place.region)) count += 1;
    if (differs(before.site, place.site)) count += 1;
    if (!sameAddress(before.address, place.address)) count += 1;
  }
  return count;
}

/** 머리의 한 줄 — 지역·공장·위치·화면 수. 미리보기 나무(고치는 중인 값)로 센다. */
export function placeStats(tree: readonly RegionNode[]): {
  regions: number;
  sites: number;
  locations: number;
  displays: number;
} {
  let sites = 0;
  let locations = 0;
  let displays = 0;
  for (const region of tree) {
    sites += region.factories.length;
    for (const factory of region.factories) {
      locations += factory.buildings.length;
      displays += factory.total;
    }
  }
  return { regions: tree.length, sites, locations, displays };
}

/** 칸을 비웠을 때 따를 값 — 위치 줄이면 이 서버의 기본값, 기본값 칸이면 "지역 미지정"(고른 말)·"본 서버"(주소는 없음). */
export function inheritedPlace(
  draft: SiteDraft,
  labels: SiteLabels = DEFAULT_SITE_LABELS,
): { region: string; site: string; address: string } {
  return {
    region: draft.region.trim() || unnamedRegion(labels),
    site: draft.site.trim() || LOCAL_SITE_NAME,
    address: hasAddress(draft.address) ? addressLine(draft.address) : '',
  };
}

/** 지역·공장 칸이 고를 이름 — 지금 쓰는 이름(고치는 중인 것 포함)을 가나다순으로. */
export function placeNames(draft: SiteDraft, entries: readonly SiteEntry[]): { regions: string[]; sites: string[] } {
  const regions = new Set<string>();
  const sites = new Set<string>();
  const add = (region: string, site: string): void => {
    if (region.trim()) regions.add(region.trim());
    if (site.trim()) sites.add(site.trim());
  };
  add(draft.region, draft.site);
  for (const place of Object.values(draft.buildings)) add(place.region, place.site);
  // 다른 서버(통합 관제)의 지역·공장 이름도 고를 수 있게 — 같은 공장을 두 서버가 나눠 맡는 일이 있다.
  for (const entry of entries) add(entry.region, entry.site);
  const sort = (values: Set<string>): string[] => [...values].sort((a, b) => compareLocationNames(a, b));
  return { regions: sort(regions), sites: sort(sites) };
}

/** 이름 바꾸기 결과를 한 줄로 — `word` 는 셋째 층의 이름(기본 "위치", 고른 말이면 "건물"·"층"). */
export function renameSummary(from: string, to: string, result: LocationChangeResult, word = '위치'): string {
  return result.merged
    ? `'${from}' 화면 ${result.displays}대를 '${to}' ${withParticle(word, '으로')} 합쳤습니다`
    : `'${from}' 을(를) '${to}' 로 바꿨습니다 — 화면 ${result.displays}대`;
}

/** 바꿀 이름을 쓸 수 있는가. 쓸 수 없으면 그 까닭(빈 이름·같은 이름), 쓸 수 있으면 null. */
export function renameProblem(from: string, draft: string): string | null {
  const to = draft.trim();
  if (to === '') return '새 이름을 적으세요';
  if (to === from) return '지금 이름과 같습니다';
  if (to.length > 200) return '위치 이름은 200자까지입니다';
  return null;
}
